// 숨은 IME 입력칸(`ime-capture`)에서 **확정된 글자만** 골라 원격으로 보낼 텍스트를 만든다.
//
// 🔴 왜 필요한가 — 한글이 입력칸에 들어오는 방식이 엔진마다 다르다.
//   - Chromium(웹 브라우저·Windows 앱 WebView2): `compositionstart/update/end` 가 나온다.
//     → `compositionend` 의 `data` 가 곧 확정 글자다(종전 경로).
//   - **macOS WebKit(Safari·Mac 앱 WKWebView) + Mac 한글 입력기**: 조합 이벤트가 **한 번도 나오지 않는다**.
//     대신 `input` 이벤트로 글자를 넣고(`insertText` "ㄱ") 조합이 진행되는 동안
//     **마지막 글자를 바꿔 끼운다**(`insertReplacementText` "가" → "간" → 다음 모음에서 "가"+"나").
//     그래서 `compositionend` 만 보던 뷰어는 Mac 에서 **한글을 한 글자도 보내지 못했다**
//     (2026-09-26 Safari 26.6.2 진단 페이지 실측 — 입력칸엔 "가나다라" 가 쌓였는데 전송 0건).
//
// ⇒ 이 클래스는 두 번째 방식을 다룬다: 입력칸 값 중 **이미 보낸 글자 수**를 기억해 두고,
//   입력기가 아직 바꿔 끼울 수 있는 **마지막 한글 한 글자**만 남겨 둔 채 그 앞을 확정으로 보낸다.
//   남겨 둔 글자는 조합과 무관한 키(Enter·Space·방향키 등)를 누르거나 포커스를 잃을 때 `flush()` 로 보낸다.
//
// ⚠️ 조합 이벤트가 나오는 엔진에서는 이 경로가 **아무것도 보내지 않아야** 한다(중복 전송 방지) —
//    조합 중(`composing`)이거나 조합 계열 `inputType` 이면 건너뛴다.
// ⚠️ 붙여넣기(`insertFromPaste`)는 클립보드 공유가 따로 처리하므로 여기서 다루지 않는다.

/** 이 경로가 받아들이는 `InputEvent.inputType` — 입력기가 글자를 넣거나 바꿔 끼우는 두 가지뿐이다. */
const COMMIT_INPUT_TYPES: ReadonlySet<string> = new Set(['insertText', 'insertReplacementText']);

/**
 * 입력기가 **아직 바꿔 끼울 수 있는** 글자인지 — 한글 자모·음절이면 조합이 끝나지 않았을 수 있다.
 * 그 밖의 글자(이모지·기호 등)는 넣는 즉시 확정이므로 붙잡아 두지 않는다(늦게 가면 순서가 어긋난다).
 *   U+1100–11FF 한글 자모 · U+3130–318F 호환 자모 · U+A960–A97F 확장 A · U+AC00–D7AF 음절 · U+D7B0–D7FF 확장 B
 */
const HANGUL_RE = /[ᄀ-ᇿ㄰-㆏ꥠ-꥿가-퟿]/;

/**
 * 눌러도 조합을 **끝내지 않는** 키(`KeyboardEvent.key`) — 수정키 단독 누름.
 *
 * 🔴 Shift 를 여기 넣지 않았을 때 **쌍자음 받침·Shift 모음이 깨졌다**(2026-09-26 사용자 보고 "쌍시옷 받침이 안 된다"):
 *    `이` 다음 Shift+ㅅ(ㅆ) → Shift 누름이 `이` 를 확정하고 입력칸을 비워, 입력기가 `이`→`있` 으로 바꿔 끼울 글자가 사라졌다.
 *    ㄲ·ㄸ·ㅃ·ㅉ·ㅆ 받침과 ㅒ·ㅖ 가 모두 같은 경로다(첫소리는 붙잡은 글자가 없어 멀쩡했다).
 * 수정키와 **함께 누르는 실제 키**(Ctrl+C 의 C 등)는 여전히 확정하므로 단축키 앞 순서는 그대로다.
 */
const IME_NEUTRAL_KEYS: ReadonlySet<string> = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock']);

/** 이 키 누름이 붙잡아 둔 글자를 확정해야 하는지 — 수정키 단독이면 false. */
export function keyCommitsIme(key: string): boolean {
  return !IME_NEUTRAL_KEYS.has(key);
}

export class ImeCommitTracker {
  /** 입력칸 값 가운데 이미 원격으로 보낸 글자 수(코드 포인트 단위 — 이모지를 반으로 자르지 않는다). */
  private sent = 0;
  /** 조합 이벤트 방식(Chromium)으로 입력 중인지 — 그동안은 이 경로가 손대지 않는다. */
  private composing = false;

  /** `compositionstart` — 조합 이벤트 방식이 시작됐다. */
  compositionStart(): void {
    this.composing = true;
  }

  /** `compositionend` — 종전 경로가 확정 글자를 보내고 입력칸을 비운 뒤 부른다. */
  compositionEnd(): void {
    this.composing = false;
    this.sent = 0;
  }

  /**
   * `input` 이벤트 한 건 → 지금 확정돼 원격으로 보낼 텍스트(없으면 빈 문자열).
   * @param value      이벤트 직후 입력칸 값
   * @param inputType  `InputEvent.inputType`
   * @param isComposing `InputEvent.isComposing`
   */
  onInput(value: string, inputType: string, isComposing: boolean): string {
    const chars = Array.from(value);
    // 입력기가 글자를 지웠으면(예: 조합 중 Backspace) 보낸 위치를 값 길이에 맞춘다.
    if (chars.length < this.sent) {
      this.sent = chars.length;
    }
    if (this.composing || isComposing || !COMMIT_INPUT_TYPES.has(inputType)) {
      return '';
    }
    // 마지막 글자가 한글이면 입력기가 아직 바꿔 끼울 수 있으므로 남겨 둔다.
    let end = chars.length;
    if (end > this.sent && HANGUL_RE.test(chars[end - 1])) {
      end -= 1;
    }
    return this.take(chars, end);
  }

  /**
   * 남겨 둔 글자를 모두 확정해 돌려준다 — 조합과 무관한 키를 누를 때(그 키보다 **먼저** 보내야 순서가 맞다)와
   * 포커스를 잃을 때 부른다. 호출한 쪽이 입력칸을 비운다(그래서 보낸 위치도 0 으로 되돌린다).
   * @returns 보낼 텍스트. **`null` = 조합 이벤트 방식으로 조합 중** — 입력칸을 건드리면 조합이 깨지므로 비우지 말 것.
   */
  flush(value: string): string | null {
    if (this.composing) {
      return null;
    }
    const s = this.take(Array.from(value), Number.MAX_SAFE_INTEGER);
    this.sent = 0;
    return s;
  }

  /** chars[sent, end) 를 잘라 보내고 보낸 위치를 옮긴다. */
  private take(chars: string[], end: number): string {
    const stop = Math.min(end, chars.length);
    if (stop <= this.sent) {
      return '';
    }
    const s = chars.slice(this.sent, stop).join('');
    this.sent = stop;
    return s;
  }
}
